import jsPDF from 'jspdf';

// Professional Theme Configuration
const PROFESSIONAL_THEME = {
  colors: {
    primary: '#1e3a8a',
    secondary: '#1e40af',
    accent: '#3b82f6',
    text: '#1f2937',
    textLight: '#6b7280',
    background: '#ffffff'
  },
  fonts: {
    primary: 'Times',
    secondary: 'Helvetica'
  },
  spacing: {
    margin: 20,
    section: 15,
    line: 6,
    paragraph: 4
  }
};

class PdfGenerator {
  constructor() {
    this.doc = null;
    this.currentY = 0;
    this.theme = PROFESSIONAL_THEME;
    this.pageWidth = 210; // A4 width in mm
    this.pageHeight = 297; // A4 height in mm
    this.margin = this.theme.spacing.margin;
    this.contentWidth = this.pageWidth - (this.margin * 2);
  }

  // Initialize PDF document
  init() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.currentY = this.margin;

    // Set default font
    this.doc.setFont(this.theme.fonts.secondary, 'normal');
    this.doc.setFontSize(10);
  }

  // Add header section
  addHeader(name, title, email, phone, location) {
    // Name - Large, bold, primary color
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(26);
    this.doc.setTextColor(30, 58, 138); // Primary color

    const nameWidth = this.doc.getTextWidth(name);
    const nameX = (this.pageWidth - nameWidth) / 2;
    this.doc.text(name, nameX, this.currentY);
    this.currentY += 14;

    // Title - Medium, normal
    this.doc.setFont(this.theme.fonts.secondary, 'normal');
    this.doc.setFontSize(16);
    this.doc.setTextColor(107, 114, 128); // Text light

    const titleWidth = this.doc.getTextWidth(title);
    const titleX = (this.pageWidth - titleWidth) / 2;
    this.doc.text(title, titleX, this.currentY);
    this.currentY += 10;

    // Contact info - Small, centered
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99); // Text

    const contactInfo = `${email} | ${phone} | ${location}`;
    const contactWidth = this.doc.getTextWidth(contactInfo);
    const contactX = (this.pageWidth - contactWidth) / 2;
    this.doc.text(contactInfo, contactX, this.currentY);
    this.currentY += 15;

    // Add horizontal line
    this.doc.setDrawColor(59, 130, 246); // Accent color
    this.doc.setLineWidth(0.5);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 10;
  }

  // Add section title
  addSectionTitle(title) {
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(14);
    this.doc.setTextColor(30, 58, 138); // Primary color

    this.doc.text(title.toUpperCase(), this.margin, this.currentY);
    this.currentY += 8;

    // Add underline
    const titleWidth = this.doc.getTextWidth(title.toUpperCase());
    this.doc.setDrawColor(59, 130, 246); // Accent color
    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin, this.currentY - 2, this.margin + titleWidth, this.currentY - 2);
    this.currentY += 6;
  }

  // Add work experience entry
  addWorkExperience(position, company, period, achievements) {
    // Position and Company
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(13);
    this.doc.setTextColor(31, 41, 55); // Text

    this.doc.text(position, this.margin, this.currentY);
    this.currentY += 7;

    // Company and Period
    this.doc.setFont(this.theme.fonts.secondary, 'italic');
    this.doc.setFontSize(11);
    this.doc.setTextColor(107, 114, 128); // Text light

    this.doc.text(`${company} | ${period}`, this.margin, this.currentY);
    this.currentY += 9;

    // Achievements
    this.doc.setTextColor(75, 85, 99); // Text
    achievements.forEach(achievement => {
      const lines = this.doc.splitTextToSize(`• ${achievement}`, this.contentWidth - 10);
      lines.forEach(line => {
        this.doc.text(line, this.margin + 5, this.currentY);
        this.currentY += 5;
      });
      this.currentY += 2;
    });

    this.currentY += 6;
  }

  // Add education entry
  addEducation(degree, institution, period) {
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(13);
    this.doc.setTextColor(31, 41, 55); // Text

    this.doc.text(degree, this.margin, this.currentY);
    this.currentY += 7;

    this.doc.setFont(this.theme.fonts.secondary, 'italic');
    this.doc.setFontSize(11);
    this.doc.setTextColor(107, 114, 128); // Text light

    this.doc.text(`${institution} | ${period}`, this.margin, this.currentY);
    this.currentY += 12;
  }

  // Add skills section
  addSkills(skillsData) {
    // Group skills by category with priority ordering
    const skillsByCategory = {};
    const categoryPriority = {
      'API Development': 1,
      'Data Science': 2,
      'Machine Learning': 3,
      'Microservices': 4,
      'Architecture': 5,
      'DevOps': 6,
      'Languages': 7,
      'Databases': 8,
      'Tools': 9
    };

    skillsData.forEach(skill => {
      const category = skill.category[0] || 'Other';
      if (!skillsByCategory[category]) {
        skillsByCategory[category] = [];
      }
      skillsByCategory[category].push(skill);
    });

    // Sort categories by priority and display
    const sortedCategories = Object.entries(skillsByCategory).sort(([a], [b]) => {
      const priorityA = categoryPriority[a] || 999;
      const priorityB = categoryPriority[b] || 999;
      return priorityA - priorityB;
    });

    sortedCategories.forEach(([category, skills]) => {
      this.checkPageBreak(20);

      // Category header with underline
      this.doc.setFont(this.theme.fonts.primary, 'bold');
      this.doc.setFontSize(11);
      this.doc.setTextColor(30, 58, 138); // Primary color

      this.doc.text(category, this.margin, this.currentY);

      // Add underline
      const categoryWidth = this.doc.getTextWidth(category);
      this.doc.setDrawColor(59, 130, 246); // Accent color
      this.doc.setLineWidth(0.2);
      this.doc.line(this.margin, this.currentY + 1, this.margin + categoryWidth, this.currentY + 1);

      this.currentY += 6;

      // Skills in category - display as bullet points in rows
      this.doc.setFont(this.theme.fonts.secondary, 'normal');
      this.doc.setFontSize(9);
      this.doc.setTextColor(75, 85, 99); // Text

      // Sort skills by competency (highest first)
      const sortedSkills = skills.sort((a, b) => (b.competency || 0) - (a.competency || 0));

      // Display skills in two columns with consistent spacing
      const maxSkills = 10;
      const displaySkills = sortedSkills.slice(0, maxSkills);
      const skillsPerColumn = Math.ceil(displaySkills.length / 2);
      const columnWidth = (this.contentWidth - 10) / 2;

      for (let i = 0; i < displaySkills.length; i++) {
        const skill = displaySkills[i];
        const column = Math.floor(i / skillsPerColumn);
        const row = i % skillsPerColumn;

        const xPos = this.margin + 5 + (column * columnWidth);
        const yPos = this.currentY + (row * 4.5);

        const skillText = `• ${skill.title}`;

        // Ensure skill text fits within column width
        const truncatedText = this.doc.splitTextToSize(skillText, columnWidth - 5);
        if (truncatedText.length > 0) {
          this.doc.text(truncatedText[0], xPos, yPos);
        }
      }

      this.currentY += Math.max(skillsPerColumn * 4.5, 20) + 8; // Consistent spacing
    });

    this.currentY += 3; // Extra space after skills section
  }

  // Add certifications
  addCertifications(certifications) {
    certifications.forEach(cert => {
      this.doc.setFont(this.theme.fonts.secondary, 'bold');
      this.doc.setFontSize(12);
      this.doc.setTextColor(31, 41, 55); // Text

      this.doc.text(`• ${cert.title}`, this.margin, this.currentY);
      this.currentY += 6;

      if (cert.id) {
        this.doc.setFont(this.theme.fonts.secondary, 'normal');
        this.doc.setFontSize(10);
        this.doc.setTextColor(107, 114, 128); // Text light

        this.doc.text(`  Credential ID: ${cert.id}`, this.margin + 5, this.currentY);
        this.currentY += 9;
      } else {
        this.currentY += 5;
      }
    });
  }

  // Check if we need a new page
  checkPageBreak(requiredSpace = 20) {
    if (this.currentY + requiredSpace > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.currentY = this.margin;
      return true;
    }
    return false;
  }

  // Generate complete resume PDF
  generateResume(resumeData) {
    try {
      this.init();

      // Header
      this.addHeader(
        resumeData.name,
        resumeData.title,
        resumeData.email,
        resumeData.phone,
        resumeData.location
      );

      // Summary (if provided)
      if (resumeData.summary) {
        this.addSectionTitle('Professional Summary');

        this.doc.setFont(this.theme.fonts.secondary, 'normal');
        this.doc.setFontSize(10);
        this.doc.setTextColor(75, 85, 99);

        const summaryLines = this.doc.splitTextToSize(resumeData.summary, this.contentWidth);
        summaryLines.forEach(line => {
          this.doc.text(line, this.margin, this.currentY);
          this.currentY += 5;
        });
        this.currentY += 8;
      }

      // Experience
      if (resumeData.experience && resumeData.experience.length > 0) {
        this.checkPageBreak(30);
        this.addSectionTitle('Professional Experience');

        resumeData.experience.forEach(exp => {
          this.checkPageBreak(25);
          this.addWorkExperience(
            exp.position,
            exp.company,
            exp.period,
            exp.achievements || []
          );
        });
      }

      // Education
      if (resumeData.education && resumeData.education.length > 0) {
        this.checkPageBreak(20);
        this.addSectionTitle('Education');

        resumeData.education.forEach(edu => {
          this.addEducation(edu.degree, edu.institution, edu.period);
        });
      }

      // Skills
      if (resumeData.skills && resumeData.skills.length > 0) {
        this.checkPageBreak(40);
        this.addSectionTitle('Technical Skills');
        this.addSkills(resumeData.skills);
      }

      // Certifications
      if (resumeData.certifications && resumeData.certifications.length > 0) {
        this.checkPageBreak(30);
        this.addSectionTitle('Certifications');
        this.addCertifications(resumeData.certifications);
      }

      return this.doc;

    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF resume');
    }
  }

  // Save the PDF
  save(filename = 'resume.pdf') {
    if (!this.doc) {
      throw new Error('PDF not generated yet');
    }
    this.doc.save(filename);
  }
}

// Export function for easy use
export const generateProfessionalResume = (resumeData) => {
  const generator = new PdfGenerator();
  const doc = generator.generateResume(resumeData);
  return doc;
};

export default PdfGenerator;
